import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import StandardNavbar from '../components/StandardNavbar';
import Footer from '../components/Footer';
import { ArrowLeft, Send, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

function Arrepentimiento() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [paginaCMS, setPaginaCMS] = useState(null);
  const [loadingCMS, setLoadingCMS] = useState(true);
  const [seccionesAbiertas, setSeccionesAbiertas] = useState({});
  const [formData, setFormData] = useState({
    email: '',
    numeroReserva: '',
    fechaCompra: '',
    fechaViaje: '',
    nombreTitular: '',
    numeroDocumento: '',
    emailReserva: '',
    motivo: ''
  });

  // Cargar contenido desde CMS
  useEffect(() => {
    const fetchContenidoCMS = async () => {
      try {
        setLoadingCMS(true);
        console.log('🔍 Cargando página CMS de arrepentimiento...');
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/paginas-cms/arrepentimiento`);
        
        console.log('📥 Response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Contenido CMS cargado:', data);
          setPaginaCMS(data);
          
          // Abrir la primera sección por defecto
          if (data.secciones && data.secciones.length > 0) {
            setSeccionesAbiertas({ [data.secciones[0].id]: true });
          }
        } else {
          console.warn('⚠️ Página CMS no encontrada, usando contenido por defecto');
        }
      } catch (error) {
        console.error('❌ Error cargando contenido CMS:', error);
      } finally {
        setLoadingCMS(false);
      }
    };

    fetchContenidoCMS();
  }, []);

  const toggleSeccion = (seccionId) => {
    setSeccionesAbiertas(prev => ({
      ...prev,
      [seccionId]: !prev[seccionId]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/arrepentimiento`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setEnviado(true);
        
        // Resetear formulario después de 3 segundos
        setTimeout(() => {
          setEnviado(false);
          setFormData({
            email: '',
            numeroReserva: '',
            fechaCompra: '',
            fechaViaje: '',
            nombreTitular: '',
            numeroDocumento: '',
            emailReserva: '',
            motivo: ''
          });
        }, 3000);
      } else {
        throw new Error('Error al enviar');
      }
    } catch (error) {
      console.error('Error enviando formulario:', error);
      alert('Error al enviar el formulario. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Contenido por defecto si no hay CMS
  const contenidoPorDefecto = {
    titulo: 'Botón de Arrepentimiento',
    descripcion: 'Formulario de arrepentimiento para reservas ya abonadas',
    secciones: [
      {
        id: 1,
        titulo: '¿Te arrepentiste de tu compra?',
        contenido: `
          <p>De acuerdo al derecho de arrepentimiento, podés cancelar tu compra dentro de los 10 días corridos de haberla realizado por los canales no presenciales (online, teléfono o mail).</p>
          <p><strong>* Según Resolución 329/2020 ANAC</strong> el derecho de arrepentimiento no aplica para las cancelaciones de los vuelos, en este caso serán las aerolíneas las que indicarán las condiciones según el ticket aéreo.</p>
          <p><strong>IMPORTANTE:</strong> Los paquetes turísticos comercializados en la sección "Último momento" constituyen ofertas con fechas muy próximas al viaje no pudiendo el pasajero solicitar el arrepentimiento de dicha compra conforme a los plazos para el viaje.</p>
          <p>Dentro de las 24hs de enviado este formulario recibirá una respuesta por el mismo canal que lo solicitó (e-Mail).</p>
        `
      },
      {
        id: 2,
        titulo: 'Si realizaste la reserva por nuestra web:',
        contenido: `
          <p>Verifica los datos de tu viaje, ingresando a la sección "Mis Reservas", identifica el viaje que queres cancelar y el Número de reserva del mismo para poder completar los siguientes campos.</p>
        `
      }
    ]
  };

  const contenido = paginaCMS || contenidoPorDefecto;

  return (
    <>
      <Helmet>
        <title>{contenido.titulo} | Noche y Día Turismo</title>
        <meta name="description" content={contenido.descripcion} />
      </Helmet>
      
      <StandardNavbar />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#2AA8B0] to-[#1E3A5F] text-white py-12">
          <div className="container mx-auto px-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft size={20} />
              Volver
            </button>
            <h1 className="text-4xl font-bold">{contenido.titulo}</h1>
            <p className="text-blue-100 text-lg mt-2">{contenido.descripcion}</p>
          </div>
        </div>

        {/* Contenido */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Información desde CMS - Secciones Desplegables */}
            {!loadingCMS && contenido.secciones && contenido.secciones.map((seccion, index) => (
              <div 
                key={seccion.id}
                className={`${
                  index === 0 
                    ? 'bg-yellow-50 border-l-4 border-yellow-400' 
                    : 'bg-white rounded-lg shadow-md'
                } overflow-hidden`}
              >
                <button
                  onClick={() => toggleSeccion(seccion.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {index === 0 && (
                      <AlertCircle className="text-yellow-400 flex-shrink-0" size={24} />
                    )}
                    <h2 className="text-xl font-semibold text-gray-900">
                      {seccion.titulo}
                    </h2>
                  </div>
                  {seccionesAbiertas[seccion.id] ? (
                    <ChevronUp size={24} className="text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown size={24} className="text-gray-400 flex-shrink-0" />
                  )}
                </button>
                
                {seccionesAbiertas[seccion.id] && (
                  <div className="px-6 py-4 border-t border-gray-200">
                    <div 
                      className="prose prose-gray max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{ __html: seccion.contenido }}
                    />
                  </div>
                )}
              </div>
            ))}

            {/* Formulario - SIEMPRE ESTÁTICO */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Completá el formulario
              </h2>
              
              {enviado ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    ¡Formulario enviado exitosamente!
                  </h3>
                  <p className="text-gray-600">
                    Recibirás una respuesta dentro de las 24hs por e-Mail.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      * E-Mail
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="tu@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      * Número de reserva
                    </label>
                    <input
                      type="text"
                      name="numeroReserva"
                      value={formData.numeroReserva}
                      onChange={handleInputChange}
                      required
                      placeholder="1234"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        * Fecha de compra
                      </label>
                      <input
                        type="date"
                        name="fechaCompra"
                        value={formData.fechaCompra}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        * Fecha de viaje
                      </label>
                      <input
                        type="date"
                        name="fechaViaje"
                        value={formData.fechaViaje}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        * Nombre y Apellido del titular
                      </label>
                      <input
                        type="text"
                        name="nombreTitular"
                        value={formData.nombreTitular}
                        onChange={handleInputChange}
                        required
                        placeholder="Nombre del titular de la compra"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        * Número de documento del Titular
                      </label>
                      <input
                        type="text"
                        name="numeroDocumento"
                        value={formData.numeroDocumento}
                        onChange={handleInputChange}
                        required
                        placeholder="1234"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      * E-mail con el que se realizó la reserva
                    </label>
                    <input
                      type="email"
                      name="emailReserva"
                      value={formData.emailReserva}
                      onChange={handleInputChange}
                      required
                      placeholder="reserva@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      * Motivo de arrepentimiento
                    </label>
                    <textarea
                      name="motivo"
                      value={formData.motivo}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent"
                      placeholder="Describa el motivo de su arrepentimiento..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Enviar
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
}

export default Arrepentimiento;